import { authApi } from '@shared/api-client';
import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useFormError } from '@/hooks/use-form-errors';
import { useSession } from '@/hooks/use-session';
import { GoogleSignIn, googleSigninError } from '@/components/google-signin';

export const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (
    location.state as {
      from?: { pathname: string; search?: string; hash?: string };
    } | null
  )?.from;
  const googleError = googleSigninError(location.search);
  const { refresh } = useSession();
  const { error, captureError, clearError } = useFormError();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [needsConfirmation, setNeedsConfirmation] = React.useState<
    string | null
  >(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    setNeedsConfirmation(null);
    setIsSubmitting(true);

    const form = new FormData(event.currentTarget);
    const email = String(form.get('email'));

    try {
      await authApi.signIn({ email, password: String(form.get('password')) });

      // The session cookie is set; re-reading /user is what makes the guard let
      // us through, so navigate only once it has resolved.
      await refresh();

      const from = (location.state as { from?: { pathname: string } } | null)
        ?.from?.pathname;
      navigate(from ?? '/', { replace: true });
    } catch (err) {
      // An unconfirmed account is recoverable, so offer the way out rather than
      // leaving the person at a dead end.
      if ((err as { status?: number }).status === 403) {
        setNeedsConfirmation(email);
      }
      captureError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Sign in</CardTitle>
      </CardHeader>

      <CardContent>
        {googleError && !error ? (
          <Alert tone="error" className="mb-4">
            {googleError}
          </Alert>
        ) : null}
        <GoogleSignIn
          disabled={isSubmitting}
          returnTo={
            from
              ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`
              : undefined
          }
        />
        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <Alert tone="error">
              {error}
              {needsConfirmation ? (
                <Link
                  to={`/confirm?email=${encodeURIComponent(needsConfirmation)}`}
                  className="ml-1 font-medium text-accent underline-offset-4 hover:underline"
                >
                  Confirm your email
                </Link>
              ) : null}
            </Alert>
          ) : null}

          <Field label="Email" htmlFor="email">
            <Input
              name="email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              placeholder="you@company.com"
            />
          </Field>

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <label
                htmlFor="password"
                className="text-sm font-medium text-ink"
              >
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-ink-muted underline-offset-4 hover:text-ink hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
          </div>

          <Button type="submit" className="w-full" loading={isSubmitting}>
            Sign in
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-ink-muted">
          No account?{' '}
          <Link
            to="/signup"
            className="font-medium text-accent underline-offset-4 hover:underline"
          >
            Create one
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
